import { Eye, Trash, Download } from "lucide-react";
import { saveAs } from "file-saver";
import React, {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import Select from "react-select";
import {
  getDocuments,
  getDocumentsTypes,
  uploadDocument,
  deleteDocument,
  downloadDocument,
} from "../../../ApiHelper/url_Helpers.js";
import { docTypeOptions } from "./ProfileData.js";
import CommonTable from "../../../Components/CommonTable";
import Loader from "../../../Components/Loader";
import { RiseLoader } from "react-spinners";
import DeleteModal from "../../../Components/DeleteModal";
import { toasterSuccess, toasterError } from "../../../Components/toaster";

const Documents = forwardRef(({ onEditStatusChange, onDirtyChange }, ref) => {
  const [documents, setDocuments] = useState([]);
  const [documentstypes, setDocumentsTypes] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [docType, setDocType] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isEditable, setIsEditable] = useState(false); // Master Edit Logic
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [activeDeleteId, setActiveDeleteId] = useState(null);
  const [activeDeleteName, setActiveDeleteName] = useState("");
  const [spinner, setSpinner] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(!!selectedFile || !!docType);
    }
  }, [selectedFile, docType, onDirtyChange]);

  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      height: "44px",
      borderRadius: "0.5rem",
      borderColor: state.isFocused ? "#00B8D9" : "#d1d5db",
      boxShadow: state.isFocused ? "0 0 0 1px #00B8D9" : "none",
      "&:hover": { borderColor: "#00B8D9" },
      fontSize: "14px",
      backgroundColor: !isEditable ? "#F3F4F6" : "white",
      cursor: !isEditable ? "not-allowed" : "default",
    }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
    placeholder: (base) => ({ ...base, color: "#9ca3af" }),
  };

  const fetchDocs = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getDocuments();
      const List = await getDocumentsTypes();
      const documentOptions = List?.document_types?.map((item) => ({
        value: item.name,
        label: item.name,
      }));
      setDocumentsTypes(documentOptions || []);
      const docWrapper = res?.documents || res;
      const canEdit = docWrapper?.action?.isEdit ?? false;
      const data =
        docWrapper?.data || (Array.isArray(docWrapper) ? docWrapper : []);

      setIsEditable(true);
      onEditStatusChange(canEdit);
      setDocuments(data);
    } catch (err) {
      console.error("Fetch docs error:", err);
      setDocuments([]);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  }, []);

  useEffect(() => {
    fetchDocs();
  }, [fetchDocs]);

  const handleDelete = async () => {
    try {
      await deleteDocument(activeDeleteId);
      setDocuments((prevDocs) =>
        prevDocs.filter((doc) => doc.id !== activeDeleteId)
      );
      setIsDeleteModalOpen(false);
      setActiveDeleteId(null);
      toasterSuccess("Document deleted successfully!");
    } catch (err) {
      console.error("Delete error:", err);
      setIsDeleteModalOpen(false);
      toasterError(err.message || "Failed to delete document.");
    }
  };

  const confirmDelete = (row) => {
    setActiveDeleteId(row.id);
    setActiveDeleteName(row.file_name || row.document_type_label || "Document");
    setIsDeleteModalOpen(true);
  };

  const handleDownload = async (id, fileName) => {
    try {
      const response = await downloadDocument(id);
      saveAs(response, fileName || `document_${id}`);
      toasterSuccess("Download started successfully!");
    } catch (err) {
      console.error("Download error:", err);
      toasterError("Failed to download document.");
    }
  };

  // Exposing isEditable so Parent (MyProfile) can read it
  useImperativeHandle(ref, () => ({
    isEditable: isEditable,
    handleChildSave: async () => {
      if (!isEditable) return { success: false, error: "Editing is locked" };
      setError("");

      if (!selectedFile && !docType) return { success: true };

      if (!selectedFile || !docType) {
        setError("Please select both Document Type and File before saving.");
        return { success: false };
      }

      try {
        const formData = new FormData();
        formData.append("document_type", docType);
        formData.append("file", selectedFile);

        await uploadDocument(formData);

        setSelectedFile(null);
        setDocType("");
        setError("");
        if (fileInputRef.current) fileInputRef.current.value = "";

        await fetchDocs();
        return { success: true };
      } catch (err) {
        const msg = err.response?.data?.message || "Upload failed.";
        setError(msg);
        return { success: false, error: msg };
      }
    },
  }));

  return (
    <div className="w-full rounded-xl overflow-x-hidden max-w-full min-w-0">
      {loading ? (
        <div
          className={`flex items-center justify-center w-full h-full min-h-[350px] bg-white`}
        >
          <RiseLoader color="#35e6e9" />
        </div>
      ) : (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-sm md:text-[16px] px-2 font-semibold text-gray-800">
              Uploaded Documents List
            </h2>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto max-w-full shadow-sm">
            <CommonTable
              data={documents}
              rowKey="id"
              columns={[
                {
                  key: "document_type_label",
                  label: "Document Type",
                  render: (v, row) =>
                    row.document_type_label || row.document_type,
                },
                { key: "file_name", label: "File Name" },
              ]}
              rowActions={(row) => (
                <div className="flex justify-start gap-0">
                  <a
                    href={row.file_url}
                    target="_blank"
                    rel="noreferrer"
                    className="p-1 hover:bg-blue-50 rounded"
                  >
                    <Eye
                      size={18}
                      className="text-gray-500 cursor-pointer hover:text-[#00B8D9]"
                      title="View Document"
                    />
                  </a>
                  <button
                    onClick={() => handleDownload(row.id, row.file_name)}
                    className="p-1 hover:bg-blue-50 rounded"
                    title="Download Document"
                  >
                    <Download
                      size={18}
                      className="text-gray-500 cursor-pointer hover:text-[#00B8D9]"
                    />
                  </button>
                  {isEditable && (
                    <button
                      onClick={() => confirmDelete(row)}
                      className={`p-1 rounded transition-all hover:bg-red-50 text-gray-500 hover:text-red-500`}
                      title="Delete Document"
                    >
                      <Trash size={18} />
                    </button>
                  )}
                </div>
              )}
            />
          </div>

          {isEditable && (
            <div className="mt-8 pt-6 border-t">
              <h2 className="text-sm md:text-[16px] font-semibold mb-3 text-gray-800 flex items-center gap-2">
                Add New Document
              </h2>

              {error && (
                <div className="mb-4 text-red-500 text-sm font-medium bg-red-50 p-2 rounded-md border border-red-100">
                  {error}
                </div>
              )}

              <div className="flex flex-col md:flex-row gap-6 w-full min-w-0">
                <div className="flex-1 min-w-0">
                  <label className="block mb-2 text-sm font-semibold text-gray-500">
                    Document Type
                  </label>
                  <Select
                    options={documentstypes}
                    styles={customSelectStyles}
                    placeholder="Select Type..."
                    isDisabled={!isEditable}
                    value={docType   ? { value: docType, label: docType } : null}
                    onChange={(option) => {
                      setDocType(option ? option.value : "");
                      setError("");
                    }}
                    isClearable={isEditable}
                    menuPlacement="top"
                  />
                </div>

                <div className="flex-1 min-w-0">
                  <label className="block mb-2 text-sm font-semibold text-gray-500">
                    Upload File
                  </label>
                  <div className="flex items-center h-[44px] border border-gray-300 rounded-lg px-4 bg-white">
                    <input
                      type="text"
                      readOnly
                      value={
                        selectedFile ? selectedFile.name : "No file chosen"
                      }
                      className="flex-1 text-sm text-gray-400 bg-transparent outline-none overflow-hidden text-ellipsis"
                    />
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="hidden"
                      disabled={!isEditable}
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => {
                        if (isEditable) {
                          setSelectedFile(e.target.files[0]);
                          setError("");
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current.click()}
                      className="text-sm font-bold ml-2 text-[#00B8D9] hover:text-[#008ea6]"
                    >
                      Browse
                    </button>
                  </div>
                  <p className="mt-1 text-[10px] text-gray-400">
                    Accepted: PDF, JPG, PNG (Max 5MB)
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <DeleteModal
        isOpen={isDeleteModalOpen}
        toggleModal={(val) => setIsDeleteModalOpen(val)}
        handleDelete={handleDelete}
        activeDeleteId={activeDeleteName}
        setSpinner={setSpinner}
        name="Document"
      />
    </div>
  );
});

export default Documents;
